import { hasPinSession } from './pin-session';
import { requiredValue } from './oem-display';
import { vaultPolicy, openPayload, sealPayload, forgetVault } from './oem-vault';
import { oemCommandSchema, customerProjection, type OemCommand } from '@sqi/shared-schemas';
import type { ActiveSession, OemContext } from './auth';
import {
  offlineDatabase,
  cacheRecord,
  queueMutation,
  synchronizeMutationQueue,
  type PendingMutation,
} from './offline-db';
import { oemCommand, OemApiError, type OemWorkspace } from './oem-client';
function retentionSeconds() {
  return vaultPolicy();
}
const editable = new Set([
  'brief',
  'formula',
  'process',
  'feasibility',
  'packaging',
  'artwork',
  'annotation',
  'cost',
  'quote',
  'task',
  'conversation',
]);
const workspaceKey = (context: OemContext, userId: string) =>
  `oem:workspace:${userId}:${context.organizationId}:${context.projectId}`;
export async function retainVerifiedSession(session: ActiveSession) {
  if (hasPinSession() || !retentionSeconds() || session.signInMethod === 'pin') return;
  await cacheRecord({
    key: `oem:session:${session.userId}`,
    organizationId: session.organizationId,
    userId: session.userId,
    entityType: 'oem:session',
    entityId: session.userId,
    version: 1,
    payload: session,
    syncedAt: new Date().toISOString(),
  });
}
export async function cachedSession(userId: string): Promise<ActiveSession | null> {
  if (hasPinSession()) return null;
  const row = await offlineDatabase.records.get(`oem:session:${userId}`);
  if (
    !row ||
    !retentionSeconds() ||
    Date.now() - Date.parse(row.syncedAt) > requiredValue(retentionSeconds()) * 1000
  )
    return null;
  const value = await openPayload<ActiveSession>(userId, row.organizationId, row.payload);
  if (value.signInMethod === 'pin') return null;
  const contexts = value.contexts.filter(
    (c) => Boolean(c.validUntil) && Date.parse(requiredValue(c.validUntil)) > Date.now(),
  );
  return { ...value, contexts, organizationContexts: [], customerContexts: [] };
}
export async function retainWorkspace(
  context: OemContext,
  userId: string,
  workspace: OemWorkspace,
) {
  if (hasPinSession() || !retentionSeconds()) return;
  await offlineDatabase.records
    .filter(
      (row) =>
        row.userId === userId &&
        row.entityType === 'oem:workspace' &&
        row.key !== workspaceKey(context, userId),
    )
    .delete();
  const records = workspace.records
    .filter(
      (r) =>
        r.createdBy === userId &&
        editable.has(r.kind) &&
        !workspace.decisions.some((d) => d.recordId === r.id && d.action === 'submit'),
    )
    .map((r) => ({
      ...r,
      content: Object.fromEntries(
        Object.entries(r.content).filter(([k]) => !['fileId', 'reportId', 'dielineId'].includes(k)),
      ),
    }));
  const shared = workspace.records
    .filter(
      (r) =>
        ['brief', 'task', 'conversation'].includes(r.kind) && !records.some((x) => x.id === r.id),
    )
    .map((r) => ({ ...r, content: customerProjection(r.content) }));
  await cacheRecord({
    key: workspaceKey(context, userId),
    organizationId: context.organizationId,
    userId,
    entityType: 'oem:workspace',
    entityId: context.projectId,
    version: 1,
    payload: { ...workspace, records: [...records, ...shared], decisions: [], order: null },
    syncedAt: new Date().toISOString(),
  });
}
export async function cachedWorkspace(context: OemContext, userId: string) {
  if (hasPinSession()) throw new OemApiError('online_required', 403);
  const row = await offlineDatabase.records.get(workspaceKey(context, userId));
  if (
    !retentionSeconds() ||
    !row ||
    Date.now() - Date.parse(row.syncedAt) > requiredValue(retentionSeconds()) * 1000 ||
    !context.validUntil ||
    Date.parse(context.validUntil) <= Date.now()
  )
    throw new OemApiError('offline_scope_expired', 403);
  return {
    workspace: await openPayload<OemWorkspace>(userId, context.organizationId, row.payload),
    syncedAt: row.syncedAt,
  };
}
export async function retainDraft(
  context: OemContext,
  userId: string,
  action: string,
  body: OemCommand,
) {
  if (hasPinSession()) throw new OemApiError('online_required', 403);
  if (!retentionSeconds()) throw new OemApiError('offline_policy_unconfigured', 403);
  const kind = String(body.payload.kind);
  if (context.role === 'production' && kind !== 'feasibility')
    throw new OemApiError('production_feasibility_only', 403);
  if (
    !['create', 'save_draft'].includes(action) ||
    !editable.has(kind) ||
    !context.actions.includes(action) ||
    !context.validUntil ||
    Date.parse(context.validUntil) <= Date.now()
  )
    throw new OemApiError('online_only_action', 403);
  const parsed = oemCommandSchema.parse(body);
  if (!(await import('@sqi/shared-schemas')).validateOemPayload(action, body.payload).success)
    throw new OemApiError('validation_error', 400);
  if (action === 'save_draft') {
    const cached = await cachedWorkspace(context, userId);
    const record = cached.workspace.records.find((r) => r.id === body.target?.id);
    if (record?.createdBy !== userId) throw new OemApiError('own_draft_required', 403);
  }
  return queueMutation({
    organizationId: context.organizationId,
    userId,
    entityType: `oem:draft:${kind}`,
    entityId: body.target?.id ?? body.idempotencyKey,
    operation: action === 'create' ? 'create' : 'update',
    baseVersion: body.target?.version ?? 0,
    idempotencyKey: body.idempotencyKey,
    payload: { projectId: context.projectId, action, body: parsed },
  });
}
export async function synchronizeDrafts(context: OemContext, userId: string) {
  if (hasPinSession()) throw new OemApiError('online_required', 403);
  return synchronizeMutationQueue(
    context.organizationId,
    userId,
    async (mutation: PendingMutation) => {
      const payload = mutation.payload as {
        projectId: string;
        action: string;
        body: OemCommand;
      };
      if (payload.projectId !== context.projectId)
        return { state: 'skip', error: 'different_project_sealed' };
      try {
        await oemCommand(payload.action, payload.body, context);
        return { state: 'applied' };
      } catch (e) {
        if (e instanceof OemApiError && e.status === 409) {
          const { loadWorkspace } = await import('./oem-client');
          const current = await loadWorkspace(context);
          const original = current.records.find((r) => r.id === payload.body.target?.id);
          if (!original) return { state: 'retry', error: 'current_target_unavailable' };
          const latest = requiredValue(
            current.records
              .filter((r) => r.rootId === original.rootId)
              .sort((a, b) => b.version - a.version)[0],
          );
          return {
            state: 'conflict',
            error: e.code,
            serverVersion: latest.version,
            serverPayload: {
              record: latest,
              submitted: current.decisions.some(
                (d) => d.recordId === latest.id && d.action === 'submit',
              ),
            },
          };
        }
        if (e instanceof OemApiError && [401, 403].includes(e.status)) {
          await offlineDatabase.records.filter((row) => row.userId === userId).delete();
          dispatchEvent(new Event('sqi-session-changed'));
        }
        return { state: 'retry', error: e instanceof Error ? e.message : 'transport_unavailable' };
      }
    },
  );
}
export async function sealCachedIdentity(userId: string) {
  for (const table of [offlineDatabase.mutations, offlineDatabase.quarantine])
    for (const row of await table.filter((row) => row.userId === userId).toArray()) {
      try {
        await table.update(row.id, {
          payload: await sealPayload(userId, row.organizationId, row.payload),
        });
      } catch {
        if (row.entityType.startsWith('oem:')) throw new Error('identity_sealing_unavailable');
      }
    }
  for (const row of await offlineDatabase.conflicts
    .filter((row) => row.userId === userId)
    .toArray())
    await offlineDatabase.conflicts.update(row.id, {
      localPayload: await sealPayload(userId, row.organizationId, row.localPayload),
      serverPayload: await sealPayload(userId, row.organizationId, row.serverPayload),
    });
  await offlineDatabase.records.filter((row) => row.userId === userId).delete();
  forgetVault(userId);
}
export async function resolveOemConflict(
  context: OemContext,
  userId: string,
  conflictId: string,
  resolution: 'keep_server' | 'branch' | 'manual_merge',
  merged?: Record<string, unknown>,
) {
  let resolvedMutationKey: string | undefined;
  const conflict = await offlineDatabase.conflicts.get(conflictId);
  if (conflict?.userId !== userId || conflict.organizationId !== context.organizationId)
    throw new OemApiError('scope_denied', 403);
  const local = await openPayload<{
    projectId: string;
    action: string;
    body: OemCommand;
  }>(userId, context.organizationId, conflict.localPayload);
  if (local.projectId !== context.projectId) throw new OemApiError('scope_denied', 403);
  const current = await openPayload<{
    record: import('./oem-client').OemRecord;
    submitted: boolean;
  }>(userId, context.organizationId, conflict.serverPayload);
  if (resolution !== 'keep_server') {
    if (!navigator.onLine) throw new OemApiError('online_required', 503);
    if (!current.record) throw new OemApiError('current_target_unavailable', 409);
    if (resolution === 'manual_merge' && (current.submitted || current.record.createdBy !== userId))
      throw new OemApiError('online_revision_required', 403);
    const content = resolution === 'manual_merge' ? merged : local.body.payload.content;
    if (!content) throw new OemApiError('merged_payload_required', 400);
    const mutationId = crypto.randomUUID(),
      action = resolution === 'branch' ? 'create' : 'save_draft';
    const body = {
      ...local.body,
      idempotencyKey: mutationId,
      target:
        resolution === 'branch'
          ? null
          : {
              id: current.record.id,
              kind: current.record.kind,
              version: current.record.version,
              hash: current.record.hash,
            },
      payload: { kind: current.record.kind, content },
    };
    if (
      !context.actions.includes(action) ||
      !(await import('@sqi/shared-schemas')).validateOemPayload(action, body.payload).success
    )
      throw new OemApiError('validation_error', 400);
    resolvedMutationKey = mutationId;
    await queueMutation({
      organizationId: context.organizationId,
      userId,
      entityType: conflict.entityType,
      entityId: resolution === 'branch' ? mutationId : current.record.id,
      operation: resolution === 'branch' ? 'create' : 'update',
      baseVersion: resolution === 'branch' ? 0 : current.record.version,
      idempotencyKey: mutationId,
      payload: { projectId: context.projectId, action, body },
    });
  }
  // Later drafts over the stale aggregate remain sealed for explicit review; never replay them automatically.
  for (const row of await offlineDatabase.mutations
    .where('[organizationId+userId]')
    .equals([context.organizationId, userId])
    .toArray())
    if (row.entityId === conflict.entityId && row.idempotencyKey !== resolvedMutationKey) {
      await offlineDatabase.quarantine.put({
        ...row,
        lastError: 'successor_conflict_review_required',
      });
      await offlineDatabase.mutations.delete(row.id);
    }
  await offlineDatabase.conflicts.delete(conflict.id);
  dispatchEvent(new Event('sqi-offline-queue-changed'));
}
